import React, { useState, useEffect } from 'react';
import { FileText, CheckSquare, Download, Tag, Clock } from 'lucide-react';
import { getRecordFiles, getTaskNotes, getTaskHistory } from '../api/chatAI';

export default function CaseProfile({ profile, onSelectRecord }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [taskNotes, setTaskNotes] = useState({});
  const [loadingNotes, setLoadingNotes] = useState({});
  const [taskHistory, setTaskHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [historyModalTask, setHistoryModalTask] = useState(null);

  const handleMarkRead = async (commId) => {
    try {
      await fetch(`/api/chat-ai/comms/mark-read/${commId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('lf_token')}` }
      });
    } catch (e) { /* non-blocking */ }
  };

  const fetchTaskNotes = async (taskId) => {
    if (taskNotes[taskId] || loadingNotes[taskId]) return;
    setLoadingNotes(prev => ({ ...prev, [taskId]: true }));
    try {
      const data = await getTaskNotes(taskId);
      setTaskNotes(prev => ({ ...prev, [taskId]: data.notes || [] }));
    } catch(e) { console.error('[TaskNotes] fetch error:', e); }
    finally { setLoadingNotes(prev => ({ ...prev, [taskId]: false })); }
  };

  const fetchTaskHistory = async (taskId) => {
    if (taskHistory[taskId] || loadingHistory[taskId]) return;
    setLoadingHistory(prev => ({ ...prev, [taskId]: true }));
    try {
      const data = await getTaskHistory(taskId);
      setTaskHistory(prev => ({ ...prev, [taskId]: data.history || [] }));
    } catch(e) { console.error('[TaskHistory] fetch error:', e); }
    finally { setLoadingHistory(prev => ({ ...prev, [taskId]: false })); }
  };

  const initials = profile.title
    ? profile.title.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : '?';

  useEffect(() => {
    // Clear cached notes/history on profile change so they reload fresh
    setTaskNotes({});
    setTaskHistory({});
    setExpandedTaskId(null);
    if (activeTab === 'documents') {
      setLoadingFiles(true);
      getRecordFiles(profile.record_id)
        .then(data => setFiles(data.files || []))
        .catch(() => setFiles([]))
        .finally(() => setLoadingFiles(false));
    }
  }, [activeTab, profile.record_id, profile]);

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 text-center border-b border-gray-800">
        <div className="w-14 h-14 rounded-full bg-purple-800 flex items-center justify-center text-lg font-bold text-purple-200 mx-auto mb-2">
          {initials}
        </div>
        <h2 className="text-white font-semibold text-sm">{profile.title}</h2>
        <p className="text-gray-400 text-xs mt-0.5">{profile.record_type_name} · {profile.stage_name || 'No stage'}</p>
        {profile.reference_number && (
          <p className="text-gray-500 text-xs font-mono mt-0.5">{profile.reference_number}</p>
        )}

        {/* Stage Progress Banner */}
        {profile.workflow_stages && profile.workflow_stages.length > 0 && (() => {
          const stages = profile.workflow_stages;
          const currentIdx = stages.findIndex(s => s.is_current);
          let displayStages = stages;
          let truncated = false;
          if (stages.length > 6 && currentIdx >= 0) {
            truncated = true;
            const pieces = [];
            // first 2
            pieces.push(...stages.slice(0, 2));
            if (currentIdx > 2) pieces.push({ name: '...', _ellipsis: true, id: 'e1' });
            // current (if not already included)
            if (currentIdx >= 2) pieces.push(stages[currentIdx]);
            if (currentIdx < stages.length - 2) pieces.push({ name: '...', _ellipsis: true, id: 'e2' });
            // last 1 (if not current)
            if (currentIdx < stages.length - 1) pieces.push(stages[stages.length - 1]);
            // deduplicate
            const seen = new Set();
            displayStages = pieces.filter(s => {
              const key = s.id;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          }
          return (
            <div className="flex items-stretch mt-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {displayStages.map((s, i) => {
                if (s._ellipsis) return (
                  <div key={s.id} className="flex items-center px-1">
                    <span className="text-gray-600 text-[10px]">•••</span>
                  </div>
                );
                const isCurrent = s.is_current;
                const isPast = currentIdx >= 0 && s.sort_order < stages[currentIdx].sort_order;
                const bgColor = isCurrent ? 'bg-purple-600' : isPast ? 'bg-green-700' : 'bg-gray-800';
                const textColor = isCurrent ? 'text-white font-semibold' : isPast ? 'text-green-100' : 'text-gray-500';
                const isLast = i === displayStages.length - 1 || displayStages[i + 1]?._ellipsis;
                return (
                  <div key={s.id} className="relative flex items-center flex-shrink-0" style={{ marginRight: i < displayStages.length - 1 ? '2px' : '0' }}>
                    <div
                      className={`${bgColor} ${textColor} flex items-center text-[10px] whitespace-nowrap select-none`}
                      style={{
                        padding: '3px 14px 3px 12px',
                        clipPath: i === 0
                          ? 'polygon(0 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 0 100%)'
                          : isLast
                          ? 'polygon(6px 0, 100% 0, 100% 100%, 6px 100%, 0 50%)'
                          : 'polygon(6px 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 6px 100%, 0 50%)',
                      }}
                    >
                      {isPast && <span className="mr-1 text-[8px]">✓</span>}
                      {s.name}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 flex-shrink-0">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'documents', label: `Docs (${profile.files?.length || 0})` },
          { id: 'tasks', label: `Tasks (${profile.open_tasks?.length || 0})` },
          { id: 'comms', label: `Comms (${profile.comms?.filter(c => !c.is_read && c.direction === 'inbound').length || 0} unread)` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 text-[11px] font-medium transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'border-purple-500 text-purple-300'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div className="p-3 space-y-3">
            {/* Linked Contact Mini-Card (Intake records only) */}
            {profile.linked_contact && (
              <div
                className="bg-gray-800/60 rounded-xl p-3 cursor-pointer hover:bg-gray-800 transition-colors"
                onClick={() => onSelectRecord && onSelectRecord({ record_id: profile.linked_contact.record_id, title: profile.linked_contact.title, record_type_name: 'Contact' })}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-purple-700 flex items-center justify-center text-[11px] font-bold text-purple-200 flex-shrink-0">
                    {(profile.linked_contact.first_name?.[0] || '').toUpperCase()}{(profile.linked_contact.last_name?.[0] || '').toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-200 text-xs font-semibold truncate">
                        {profile.linked_contact.first_name} {profile.linked_contact.last_name}
                      </span>
                      {profile.linked_contact.reference_number && (
                        <span className="text-gray-500 text-[10px] font-mono flex-shrink-0">{profile.linked_contact.reference_number}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {profile.linked_contact.phone && (
                        <a href={`tel:${profile.linked_contact.phone}`} onClick={e => e.stopPropagation()} className="text-[10px] text-gray-400 hover:text-purple-300 transition-colors">
                          📞 {profile.linked_contact.phone}
                        </a>
                      )}
                      {profile.linked_contact.email && (
                        <a href={`mailto:${profile.linked_contact.email}`} onClick={e => e.stopPropagation()} className="text-[10px] text-gray-400 hover:text-purple-300 transition-colors truncate">
                          ✉ {profile.linked_contact.email}
                        </a>
                      )}
                    </div>
                    {profile.linked_contact.contact_type && Array.isArray(profile.linked_contact.contact_type) && profile.linked_contact.contact_type.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {profile.linked_contact.contact_type.map(ct => (
                          <span key={ct} className="px-1.5 py-0.5 text-[9px] bg-gray-700 text-gray-400 rounded-full">{ct}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {profile.inbound_email && (
              <div className="bg-gray-800/60 rounded-xl p-3 mb-2">
                <p className="text-gray-500 text-[10px] font-medium uppercase tracking-wide mb-1">Inbound Email</p>
                <p
                  className="text-[11px] text-gray-400 font-mono cursor-pointer hover:text-gray-200 transition-colors"
                  onClick={() => { navigator.clipboard.writeText(profile.inbound_email); }}
                  title="Click to copy"
                >{profile.inbound_email}</p>
              </div>
            )}
            {Object.keys(profile.field_data).length > 0 && (
              <div className="bg-gray-800/60 rounded-xl p-3">
                <p className="text-gray-500 text-[10px] font-medium uppercase tracking-wide mb-2">All Fields</p>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="space-y-2">
                  {Object.entries(profile.field_data).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2 text-xs border-b border-gray-700/40 pb-1">
                      <span className="text-gray-400 flex-shrink-0" style={{ minWidth: '40%' }}>{k.replace(/_/g,' ')}</span>
                      <span className="text-right" style={{ color: (v === null || v === undefined || v === '') ? '#4b5563' : '#e5e7eb' }}>
                        {(v === null || v === undefined || v === '') ? '—' : Array.isArray(v) ? v.join(', ') : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(profile.extended_data).filter(k => k !== 'last_ai_update').length > 0 && (
              <div className="bg-yellow-950/30 border border-yellow-900/40 rounded-xl p-3 space-y-1.5">
                <p className="text-yellow-400 text-[10px] font-semibold uppercase tracking-wide">AI Flags</p>
                {Object.entries(profile.extended_data)
                  .filter(([k]) => k !== 'last_ai_update' && k !== 'critical_note')
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span className="text-yellow-300/70">{k.replace(/_/g, ' ')}</span>
                      <span className="text-yellow-200 font-medium">{String(v)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Documents tab */}
        {activeTab === 'documents' && (
          <div className="p-3 space-y-2">
            {loadingFiles ? (
              <p className="text-xs text-gray-500 text-center py-4">Loading documents...</p>
            ) : files.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">No documents uploaded yet</p>
            ) : (
              files.map(f => (
                <div key={f.id} className="bg-gray-800/60 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                      <span className="text-gray-200 text-xs font-medium truncate">{f.original_name}</span>
                    </div>
                    {f.download_url && (
                      <a href={f.download_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-0.5 text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex-shrink-0 transition-colors">
                        <Download className="h-3 w-3" />
                        View
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-500">
                    <span>{formatDate(f.created_at)}</span>
                    {f.size_bytes && <span>· {formatSize(f.size_bytes)}</span>}
                  </div>
                  {f.document_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {f.document_tags.map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 text-[10px] bg-indigo-900/40 border border-indigo-700/40 text-indigo-300 rounded-full">
                          {tag.replace(/_/g,' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  {!f.document_tags?.length && f.document_category && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-gray-700 text-gray-400 rounded-full inline-block">
                      {f.document_category.replace(/_/g,' ')}
                    </span>
                  )}
                  {f.ai_summary && (
                    <p className="text-[10px] text-gray-400 italic leading-relaxed">{f.ai_summary}</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Tasks tab */}
        {activeTab === 'tasks' && (
          <div className="p-3 space-y-2">
            {profile.open_tasks.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">No open tasks</p>
            ) : (
              profile.open_tasks.map((t, i) => {
                const taskKey = t.id || i;
                const isExpanded = expandedTaskId === taskKey;
                const isOverdue = t.due_date && new Date(t.due_date) < new Date();
                return (
                  <div key={taskKey}
                    className="bg-gray-800/60 rounded-xl overflow-hidden transition-all">
                    {/* Clickable header row */}
                    <div
                      className="p-3 cursor-pointer hover:bg-gray-800 transition-colors"
                      onClick={() => {
                        const next = isExpanded ? null : taskKey;
                        setExpandedTaskId(next);
                        if (next && t.id) {
                          fetchTaskNotes(t.id);
                          fetchTaskHistory(t.id);
                        }
                      }}>
                      <div className="flex items-start gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${
                          t.priority === 'high' || t.priority === 'urgent' ? 'bg-red-400' :
                          t.priority === 'normal' ? 'bg-blue-400' : 'bg-gray-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-gray-200 text-xs font-medium truncate">
                              {t.task_number ? `#${t.task_number} - ${t.title}` : t.title}
                            </p>
                            {t.recurrence_rule && <span className="text-[10px] text-purple-400 flex-shrink-0">🔄</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              t.status === 'pending' ? 'bg-yellow-900/40 text-yellow-400' :
                              t.status === 'in_progress' ? 'bg-blue-900/40 text-blue-400' :
                              'bg-gray-700 text-gray-400'
                            }`}>{t.status}</span>
                            {t.due_date && (
                              <span className={`text-[10px] ${isOverdue ? 'text-red-400' : 'text-gray-500'}`}>
                                {isOverdue ? '⚠ Overdue · ' : 'Due '}
                                {formatDate(t.due_date)}
                              </span>
                            )}
                            {t.recurrence_rule && (
                              <span className="text-[10px] text-purple-400/70">{t.recurrence_rule}</span>
                            )}
                            <span className={`text-[10px] font-medium ${
                              t.priority === 'high' ? 'text-red-400' :
                              t.priority === 'low' ? 'text-gray-500' : 'text-blue-400'
                            }`}>{(t.priority || 'normal').toUpperCase()}</span>
                            {t.created_at && (
                              <span className="text-[10px] text-gray-600">· {formatDate(t.created_at)}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-gray-600 text-[10px] flex-shrink-0 mt-0.5">
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </div>
                    </div>
                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <div className="border-t border-gray-700/50">
                        {/* Notes section — scrollable, with author + timestamp */}
                        <div className="px-3 py-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Notes</p>
                            <button
                              onClick={(e) => { e.stopPropagation(); setHistoryModalTask({ ...t, history: taskHistory[t.id] || [] }); }}
                              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-purple-300 transition-colors"
                              title="View change history">
                              <Clock className="h-3 w-3" />
                              <span>History {taskHistory[t.id]?.length > 0 ? `(${taskHistory[t.id].length})` : ''}</span>
                            </button>
                          </div>
                          <div className="max-h-56 overflow-y-auto pr-1 space-y-2">
                            {loadingNotes[t.id] ? (
                              <p className="text-xs text-gray-600 italic">Loading notes...</p>
                            ) : (taskNotes[t.id] || []).length === 0 ? (
                              <p className="text-xs text-gray-600 italic">No notes on this task</p>
                            ) : (
                              (taskNotes[t.id] || []).map((note, ni) => (
                                <div key={note.id || ni} className="bg-gray-900/80 rounded-lg px-2.5 py-2 border border-gray-800/60">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-medium text-purple-300">
                                      {note.first_name ? `${note.first_name} ${note.last_name || ''}`.trim() : note.email}
                                    </span>
                                    <span className="text-[10px] text-gray-600">
                                      {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                      {' · '}
                                      {new Date(note.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-200 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Comms tab */}
        {activeTab === 'comms' && (
          <div className="space-y-2 p-3">
            {(!profile.comms || profile.comms.length === 0) && (
              <div className="text-gray-500 text-sm text-center py-8">No communications yet</div>
            )}
            {(profile.comms || []).map(comm => (
              <CommCard key={comm.id} comm={comm} onMarkRead={handleMarkRead} />
            ))}
          </div>
        )}
      </div>
      {/* Task History Modal */}
      {historyModalTask && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setHistoryModalTask(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-xl mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <div>
                <p className="text-xs font-semibold text-white">
                  #{historyModalTask.task_number} - {historyModalTask.title}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">Change History</p>
              </div>
              <button onClick={() => setHistoryModalTask(null)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none">×</button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {(historyModalTask.history || []).length === 0 ? (
                <p className="text-xs text-gray-600 italic text-center py-4">No changes recorded yet</p>
              ) : (
                <div className="space-y-2">
                  {(historyModalTask.history || []).map((h, i) => (
                    <div key={h.id || i} className="flex gap-3 py-2 border-b border-gray-800/60 last:border-0">
                      <div className="w-1 bg-purple-700/50 rounded-full flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <span className="text-xs text-purple-300 font-semibold capitalize flex-shrink-0">
                            {h.field_name.replace(/_/g, ' ')}
                          </span>
                          <span className="text-[10px] text-gray-500 flex-shrink-0 whitespace-nowrap">
                            {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {' · '}
                            {new Date(h.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-400 whitespace-nowrap mb-1.5">
                          {h.first_name} {h.last_name || ''}
                        </p>
                        {h.field_name === 'note_added' ? (
                          <p className="text-xs text-gray-300 italic bg-gray-800/60 rounded px-2 py-1">"{h.new_value}"</p>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {h.old_value && (
                              <span className="text-xs text-yellow-400 bg-yellow-900/20 px-2 py-0.5 rounded">
                                {h.old_value}
                              </span>
                            )}
                            <span className="text-base font-bold text-gray-300">→</span>
                            <span className="text-xs text-green-400 bg-green-900/20 px-2 py-0.5 rounded">
                              {h.new_value}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommCard({ comm, onMarkRead }) {
  const [expanded, setExpanded] = useState(false);
  const [read, setRead] = useState(comm.is_read);

  const handleExpand = async () => {
    setExpanded(e => !e);
    if (!read) {
      setRead(true);
      await onMarkRead(comm.id);
    }
  };

  return (
    <div onClick={handleExpand}
      className={`rounded-lg border p-3 cursor-pointer transition-all ${(!read && comm.direction === 'inbound') ? 'border-blue-500 bg-blue-950' : 'border-gray-700 bg-gray-900'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {!read && comm.direction === 'inbound' && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />}
          <span className="text-xs text-gray-400 uppercase">{comm.channel} · {comm.direction}</span>
        </div>
        <span className="text-xs text-gray-500">{comm.created_at ? new Date(comm.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + new Date(comm.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
      </div>
      <div className="text-sm text-white mt-1 font-medium">{comm.subject || comm.from_name || 'No subject'}</div>
      {expanded && (
        <div className="mt-2 text-sm text-gray-300 whitespace-pre-wrap border-t border-gray-700 pt-2">
          {comm.channel === 'sms' && comm.metadata ? (() => {
            const m = typeof comm.metadata === 'string' ? JSON.parse(comm.metadata) : comm.metadata;
            const lines = [];
            if (comm.body_preview || comm.body) lines.push(`Message: "${comm.body_preview || comm.body}"`);
            if (m.sms_status) {
              const s = m.sms_status;
              const label = s === 'delivered' ? '✅ Delivered' : s === 'sent' ? '📤 Sent' : s === 'failed' ? '❌ Failed' : s === 'undelivered' ? '⚠️ Undelivered' : s;
              lines.push(`Status: ${label}`);
            }
            if (m.to_number) lines.push(`To: ${m.to_number}`);
            if (m.num_segments && m.num_segments > 1) lines.push(`Segments: ${m.num_segments}`);
            if (m.error_message) lines.push(`Error: ${m.error_message}`);
            return lines.join('\n') || comm.body_preview || 'No content';
          })() : comm.channel === 'voice' && comm.metadata ? (() => {
            const m = typeof comm.metadata === 'string' ? JSON.parse(comm.metadata) : comm.metadata;
            const lines = [];
            if (m.call_status) lines.push(`Status: ${m.call_status}`);
            if (m.call_duration_seconds) lines.push(`Duration: ${m.call_duration_seconds}s`);
            if (m.worker_name || m.answered_by) lines.push(`Worker: ${m.worker_name || m.answered_by}`);
            if (m.client_number) lines.push(`Dialed: ${m.client_number}`);
            if (m.client_call_status) {
              const statusLabel = m.client_call_status === 'completed' ? '✅ Answered' :
                m.client_call_status === 'no-answer' ? '📵 No answer' :
                m.client_call_status === 'busy' ? '🔴 Busy' :
                m.client_call_status === 'failed' ? '❌ Failed' :
                m.client_call_status === 'canceled' ? '🚫 Canceled' :
                m.client_call_status;
              lines.push(`Client: ${statusLabel}`);
            }
            if (m.client_duration_seconds) lines.push(`Talk time: ${m.client_duration_seconds}s`);
            if (m.worker_phone) lines.push(`Worker phone: ${m.worker_phone}`);
            return lines.join(' | ') || 'No details';
          })() : comm.body || comm.body_preview || 'No content'}
        </div>
      )}
    </div>
  );
}
